import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Package, Plus, Trash2, User, Wrench } from 'lucide-react';

const TaskResourceAssignment = ({ taskId, projectId, onUpdate }) => {
  const [resources, setResources] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [allocationForm, setAllocationForm] = useState({
    resource_id: '',
    start: '',
    end: '',
    units: '',
    unit_type: 'hour'
  });

  useEffect(() => {
    fetchData();
  }, [taskId, projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resourcesRes, allocationsRes] = await Promise.all([
        axiosInstance.get(`/projects/${projectId}/resources`),
        axiosInstance.get(`/allocations?task_id=${taskId}`)
      ]);
      setResources(resourcesRes.data);
      setAllocations(allocationsRes.data);
    } catch (error) {
      console.error('Failed to fetch resource data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAllocation = async (e) => {
    e.preventDefault();
    
    if (!allocationForm.resource_id || !allocationForm.start || !allocationForm.end || !allocationForm.units) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }

    try {
      await axiosInstance.post('/allocations', {
        project_id: projectId,
        task_id: taskId,
        resource_id: allocationForm.resource_id,
        start: new Date(allocationForm.start).toISOString(),
        end: new Date(allocationForm.end).toISOString(),
        units: parseFloat(allocationForm.units),
        unit_type: allocationForm.unit_type
      });
      
      toast.success('Kaynak atandı');
      setDialogOpen(false);
      resetForm();
      fetchData();
      if (onUpdate) onUpdate();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Kaynak ataması başarısız';
      toast.error(errorMsg);
    }
  };

  const handleDeleteAllocation = async (allocationId) => {
    if (!window.confirm('Bu kaynak atamasını kaldırmak istediğinizden emin misiniz?')) return;
    
    try {
      await axiosInstance.delete(`/allocations/${allocationId}`);
      toast.success('Kaynak ataması kaldırıldı');
      fetchData();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const resetForm = () => {
    setAllocationForm({
      resource_id: '',
      start: '',
      end: '',
      units: '',
      unit_type: 'hour'
    });
  };

  const getResourceById = (resourceId) => {
    return resources.find(r => r.id === resourceId);
  };

  const getResourceIcon = (type) => {
    if (type === 'human') return <User size={16} />;
    if (type === 'equipment') return <Wrench size={16} />;
    return <Package size={16} />;
  };

  const getUnitLabel = (unitType) => {
    const labels = {
      hour: 'saat',
      percent: '%',
      qty: 'adet'
    };
    return labels[unitType] || unitType;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Package className="h-5 w-5" />
            Kaynaklar
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-gray-600">Yükleniyor...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg justify-between">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Kaynaklar
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setDialogOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Kaynak Ata
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {allocations.length === 0 ? (
            <p className="text-sm text-gray-600">
              Bu göreve henüz kaynak atanmamış
            </p>
          ) : (
            <div className="space-y-2">
              {allocations.map(allocation => {
                const resource = getResourceById(allocation.resource_id);
                if (!resource) return null;
                
                return (
                  <div 
                    key={allocation.id} 
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="p-2 bg-gray-100 rounded">
                        {getResourceIcon(resource.type)}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{resource.name}</div>
                        <div className="text-xs text-gray-600">
                          {allocation.units} {getUnitLabel(allocation.unit_type)} • 
                          {new Date(allocation.start).toLocaleDateString('tr-TR')} - {new Date(allocation.end).toLocaleDateString('tr-TR')}
                        </div>
                        {allocation.allocated_cost && (
                          <Badge variant="outline" className="text-xs mt-1">
                            ${allocation.allocated_cost.toFixed(2)}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteAllocation(allocation.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Allocation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Kaynak Ata</DialogTitle>
            <DialogDescription>
              Bu göreve kaynak atayın (insan, ekipman veya malzeme)
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateAllocation} className="space-y-4">
            <div className="space-y-2">
              <Label>Kaynak <span className="text-red-500">*</span></Label>
              <Select
                value={allocationForm.resource_id}
                onValueChange={(value) => setAllocationForm({...allocationForm, resource_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Kaynak seçin" />
                </SelectTrigger>
                <SelectContent>
                  {resources.map(resource => (
                    <SelectItem key={resource.id} value={resource.id}>
                      {resource.name} ({resource.type === 'human' ? 'İnsan' : resource.type === 'equipment' ? 'Ekipman' : 'Malzeme'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Başlangıç <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={allocationForm.start}
                  onChange={(e) => setAllocationForm({...allocationForm, start: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Bitiş <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={allocationForm.end}
                  onChange={(e) => setAllocationForm({...allocationForm, end: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Miktar <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  step="0.01"
                  value={allocationForm.units}
                  onChange={(e) => setAllocationForm({...allocationForm, units: e.target.value})}
                  placeholder="Örn: 20"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Birim <span className="text-red-500">*</span></Label>
                <Select
                  value={allocationForm.unit_type}
                  onValueChange={(value) => setAllocationForm({...allocationForm, unit_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hour">Saat</SelectItem>
                    <SelectItem value="percent">Yüzde (%)</SelectItem>
                    <SelectItem value="qty">Adet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setDialogOpen(false);
                  resetForm();
                }}
              >
                İptal
              </Button>
              <Button type="submit">
                Ata
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TaskResourceAssignment;
